import { BigDecimal } from "@solomon-labs/bigdecimal";
import {
    Button,
    TabsMenu,
    TabTrigger,
    Text
} from "@solomon-labs/design/components/ui";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useBalanceSeek } from "../../hooks/transactions/useBalanceSeek";
import { useErrorReturnReady } from "../../hooks/transactions/useErrorReturnReady";
import { useSuccessAutoReturn } from "../../hooks/transactions/useSuccessAutoReturn";
import { useSuccessReturnReady } from "../../hooks/transactions/useSuccessReturnReady";
import type { TransactionPhase } from "../../hooks/transactions/useTransactionPhase";
import {
    useAccessControlOrchestration as useAccessControl,
    useAnalyticsCapture,
    useSimMode,
    useWalletModalOrchestration as useWalletModal
} from "../../sim";
import { simForce } from "../../sim/forceState";
import { LEGACY_USDV_MINT, USDV_MINT } from "../../tokens/usdv";
import { collapseStyle } from "../../utils/collapseStyle";
import type { SelectOption } from "../../utils/tokenHelpers";
import { AmountInput } from "../primitives/AmountInput";
import { RateRow } from "../primitives/RateRow";
import { ReceiptBlock } from "../primitives/ReceiptBlock";
import { TokenChip } from "../primitives/TokenChip";
import { TransitionButton } from "../primitives/TransitionButton";
import { useSimCursor } from "../sim-cursor/SimCursor";
import { TransactionCTA } from "../TransactionCTA";
import type { UseSwapReturn } from "../types/swap";
import type { TokenSelectorProps } from "../types/tokenSelector";

import {
    getSwapCtaLabel,
    getSwapSuccessLabel,
    getSwapTokenIcon,
    getSwapUsdVLabelAmount,
    USDC_ICON,
    USDV_ICON
} from "./swapLabels";

const LEGACY_USDV_MINTS = [LEGACY_USDV_MINT] as const;

// Label/icon policy lives in `./swapLabels` so the disconnected landing
// `BuyWidget` shares a single source of truth without pulling this module's
// heavier deps. Re-exported here to preserve the existing
// `@solomon-apps/shared` / `./swap/SwapContent` import paths (incl. the
// USDvSwapContent test suite).
export { getSwapCtaLabel, getSwapSuccessLabel, getSwapUsdVLabelAmount };
export type {
    SwapCtaLabelInputs,
    SwapSuccessLabelInputs,
    SwapUsdVLabelAmountInputs
} from "./swapLabels";

// Receipt-row stagger keyframe — required by ReceiptBlock when staggerDelay is
// set. Inlined defensively so SwapContent is self-sufficient even if the page
// doesn't otherwise inject it (BuyWidget only mounts on the disconnected
// surface).
const RECEIPT_KEYFRAMES_CSS = `
@keyframes receipt-row-in {
    0%   { opacity: 0; transform: translateY(-8px); }
    100% { opacity: 1; transform: translateY(0); }
}
`;

interface SwapContentProps {
    swap: UseSwapReturn;
    /** Phase computed by the parent (TradeWidget) so card lock + UI agree
     *  on a single source of truth — including the buy-mode `postSuccess`
     *  celebration window. */
    phase: TransactionPhase;
    onSubmit: () => Promise<void>;
    /** Optional Jupiter picker injected by TradeWidget (which receives
     *  it from the apps/main caller). Omitting it skips the picker UI —
     *  the sim showcase doesn't need a working picker. */
    TokenSelectorComponent?: React.ComponentType<TokenSelectorProps>;
}

/**
 * Buy / Sell sub-tabs of the TradeWidget Swap mode.
 *
 * v2.0.0 — In-place phase-driven flow. The card stays mounted across phases;
 * `You Send` + `RateRow` + `You Get` collapse during pending+, a
 * `ReceiptBlock` expands inside the bordered area, and the CTA transitions
 * through `Confirm in Wallet… → Processing… → Bought/Sold X` via
 * `TransitionButton`. Mirrors the BuyFlow / SellFlow showcase in
 * `TradeWidgetShowcase.tsx` (lines 308–460 / 480–594) and matches the pattern
 * used by `BuyWidget` / `StakeForm` / `UnstakeForm` / `WithdrawForm`. Both
 * modes return to the swap form after success because current USDv is not
 * stakeable while the vault accepts the legacy token only.
 */
/**
 * Implementation of SwapContent. Exported for tests that invoke the component
 * function directly; production code should import the memoized `SwapContent`.
 */
export function SwapContentImpl({
    swap,
    phase,
    onSubmit,
    TokenSelectorComponent
}: SwapContentProps): React.ReactElement {
    const { openModal } = useWalletModal();
    const { isAllowed, isChecked } = useAccessControl();
    const legacyCapture = useAnalyticsCapture();
    const capture = swap.analyticsCapture ?? legacyCapture;

    // ── Simulated cursor (sim mode only) ─────────────────────────────
    // Purely visual overlay that points at the SEND amount input while the
    // orchestrator "types" the amount, then hops to the CTA for its scripted
    // hover→press. Gated on `sim.enabled` so production renders exactly as
    // before — no container, no layer, no moves. The `data-sim-cursor-key`
    // anchors below are inert when no cursor is mounted.
    const sim = useSimMode();
    const cursor = useSimCursor();
    const { moveTo: moveCursor, click: clickCursor, hide: hideCursor } = cursor;
    // Attach the cursor's positioned container to the (relative) root only in
    // sim mode; in production the root stays a plain flex column.
    const setContainerRef = useCallback(
        (el: HTMLDivElement | null): void => {
            cursor.containerRef.current = el;
        },
        [cursor.containerRef]
    );

    // State-reactive cursor choreography. We react to the same sim state the
    // panel already receives from `useSwapOrchestration` — no new orchestrator
    // signals needed:
    //   • focus beat (simIsFocused = the `focused` phase, ~1.3s BEFORE the
    //     `filled` typing phase) → travel to the amount input and click it, so
    //     the cursor is on the field before the value types in. Reacting to
    //     the first typed char instead landed the cursor after typing began.
    //   • CTA hover beat (demoSubmitState "hover") → travel to Submit
    //   • CTA press beat (demoSubmitState "press") → click pulse
    //   • pending/processing/success/postSuccess/error → hide (the form is
    //     replaced by the receipt/success chrome; nothing to point at).
    const focusHandledRef = useRef(false);
    const amountClickTimer = useRef<number | null>(null);
    const submitStateRef = useRef<string | undefined>(undefined);
    const simDemoSubmitState = swap.demoSubmitState;
    const isReceiptOrLaterPhase =
        phase === "pending" ||
        phase === "processing" ||
        phase === "success" ||
        phase === "postSuccess" ||
        phase === "error";
    useEffect(() => {
        if (!sim.enabled) return;

        // Hide once the flow leaves the editable form — the input + CTA the
        // cursor points at are gone (collapsed / swapped for receipt chrome).
        if (isReceiptOrLaterPhase) {
            hideCursor();
            focusHandledRef.current = false;
            if (amountClickTimer.current !== null) {
                window.clearTimeout(amountClickTimer.current);
                amountClickTimer.current = null;
            }
            submitStateRef.current = simDemoSubmitState;
            return;
        }

        // Focus beat — the `focused` phase, ahead of the `filled` typing
        // phase. Travel to the amount input (forced text I-beam — the anchor
        // is a wrapper <div> that doesn't forward data-* to its <input>) and
        // click it once the cursor has arrived. The click is delayed ~600ms
        // so it pulses on arrival (not mid-travel) and still lands inside the
        // focused→filled gap, before the value starts typing.
        if (swap.simIsFocused && !focusHandledRef.current) {
            focusHandledRef.current = true;
            moveCursor("amount", { align: "left", cursor: "text" });
            amountClickTimer.current = window.setTimeout(
                () => clickCursor(),
                600
            );
        }

        // CTA hover/press beats — fire once on each transition.
        if (simDemoSubmitState !== submitStateRef.current) {
            if (simDemoSubmitState === "hover") {
                // Anchor wraps the CTA button; force the pointer glyph.
                moveCursor("submit", { cursor: "pointer" });
            } else if (simDemoSubmitState === "press") {
                clickCursor();
            }
            submitStateRef.current = simDemoSubmitState;
        }

        return (): void => {
            if (amountClickTimer.current !== null) {
                window.clearTimeout(amountClickTimer.current);
            }
        };
    }, [
        sim.enabled,
        swap.simIsFocused,
        simDemoSubmitState,
        isReceiptOrLaterPhase,
        moveCursor,
        clickCursor,
        hideCursor
    ]);
    const [sendTokenSelectorOpen, setSendTokenSelectorOpen] = useState(false);
    const [receiveTokenSelectorOpen, setReceiveTokenSelectorOpen] =
        useState(false);

    // Receipt mount-then-grow trigger — without this the receipt wrapper
    // appears at its full 322.5px height the frame it's first rendered (no
    // transition because the element didn't exist before), causing a layout
    // shock inside the sunken while You Send + Rate are still mid-collapse.
    // We render the wrapper in collapsed state on first mount and flip to
    // expanded one frame later so the collapseStyle transition animates
    // 0 → 300 instead of popping in.
    const [receiptExpanded, setReceiptExpanded] = useState(false);
    useEffect(() => {
        if (
            phase === "pending" ||
            phase === "processing" ||
            phase === "success" ||
            phase === "postSuccess" ||
            phase === "error"
        ) {
            const r = requestAnimationFrame((): void =>
                setReceiptExpanded(true)
            );
            return (): void => cancelAnimationFrame(r);
        }
        setReceiptExpanded(false);
    }, [phase]);

    const activeSubTab = swap.mode;

    // `swap_tab_viewed` once per mount of the trade-widget swap surface.
    useEffect(() => {
        capture("swap_tab_viewed", {
            mode: swap.mode,
            entry_source: "trade_widget_route"
        });
    }, [capture, swap.mode]);

    const handleSubTabChange = (value: string): void => {
        if (value === "buy") {
            swap.handleBuyTab();
        } else if (value === "sell") {
            swap.handleSellTab();
        }
    };

    const isBuy = swap.mode === "buy";
    const sendSymbol =
        swap.sendDisplayOption?.label ??
        swap.sendCurrencySymbol ??
        (isBuy ? "USDC" : "USDv");
    const receiveSymbol =
        swap.receiveDisplayOption?.label ??
        swap.receiveCurrencySymbol ??
        (isBuy ? "USDv" : "USDC");
    const sendIcon = getSwapTokenIcon(
        sendSymbol,
        swap.sendDisplayOption?.icon ?? (isBuy ? USDC_ICON : USDV_ICON)
    );
    const receiveIcon = getSwapTokenIcon(
        receiveSymbol,
        swap.receiveDisplayOption?.icon ?? (isBuy ? USDV_ICON : USDC_ICON)
    );
    const sendRounded = isBuy;
    const sendHasChev = isBuy;
    const receiveRounded = !isBuy;
    const receiveHasChev = !isBuy;
    const sendParsed = Number(swap.sendAmount) || 0;
    const hasQuoteRouteError = !!swap.quoteError && sendParsed > 0;
    // When Jupiter has no route, surface the signal in the You Get input
    // (replaces the value with "Quote unavailable") rather than the rate
    // row. Rate row keeps its real label or `—` fallback. CTA continues
    // to own balance/decimal errors ("Insufficient" wins when both apply).
    const rateLabel =
        swap.exchangeRate !== null
            ? `1 ${sendSymbol} = ${swap.exchangeRate} ${receiveSymbol}`
            : `1 ${sendSymbol} = - ${receiveSymbol}`;
    const sendSeek = useBalanceSeek({
        amount: swap.sendAmount,
        balance: swap.maxBalanceValue,
        decimals: 2,
        onChange: swap.handleSendAmountChange,
        onMax: swap.handleMaxClick
    });

    const submitDisabled =
        swap.isErrorFeedback ||
        !swap.sendAmount ||
        new BigDecimal(swap.sendAmount || "0").lte(0) ||
        !swap.canExecuteQuote ||
        swap.isQuoteLoading ||
        swap.executeState.isLoading;

    const isPendingOrLater =
        phase === "pending" ||
        phase === "processing" ||
        phase === "success" ||
        phase === "postSuccess";
    const isReceiptOrLater = isPendingOrLater || phase === "error";
    const isProcessing = phase === "processing";
    const isPending = phase === "pending";
    const isSuccess = phase === "success" || phase === "postSuccess";
    const isPostSuccess = phase === "postSuccess";
    const isError = phase === "error";
    const errorReturnReady = useErrorReturnReady(isError);
    const successReturnReady = useSuccessReturnReady(isSuccess);
    // The "Return to Buy/Sell" CTA above lets an impatient user leave at 2s;
    // this clears the receipt on its own at 3s so the flow never parks on a
    // screen with nothing left to do. Sim mode holds the receipt for demos.
    useSuccessAutoReturn(isSuccess && !sim.enabled, swap.handleSuccessClose);

    // Snapshot drives the collapsed-state copy when present; falls back to
    // current input values during the filled phase before submit.
    const snapshot = swap.swapSnapshot;
    const displaySendAmount =
        isReceiptOrLater && snapshot ? snapshot.sendAmount : swap.sendAmount;
    const displayReceiveAmount =
        isReceiptOrLater && snapshot
            ? snapshot.receiveAmount
            : swap.receiveAmountDisplay || "";
    const displaySendSymbol =
        isReceiptOrLater && snapshot
            ? snapshot.sendSymbol || sendSymbol
            : sendSymbol;
    const displayReceiveSymbol =
        isReceiptOrLater && snapshot
            ? snapshot.receiveSymbol || receiveSymbol
            : receiveSymbol;
    const displaySendIcon =
        isReceiptOrLater && snapshot?.sendIcon ? snapshot.sendIcon : sendIcon;
    const displayReceiveIcon =
        isReceiptOrLater && snapshot?.receiveIcon
            ? snapshot.receiveIcon
            : receiveIcon;

    // Phase-driven labels (Buy vs Sell mode-conditional). Form-view
    // labels read as the action ("Sell" → the token you're giving up;
    // "Buy" → the token you're acquiring) so the swap reads as
    // "Sell USDC, Buy USDv" without needing arrows or copy.
    const sendLabel = isReceiptOrLater
        ? isError
            ? isBuy
                ? "Did not send"
                : "Did not send"
            : isSuccess
              ? "Sent"
              : isBuy
                ? "Sending..."
                : "Sending..."
        : "Sell";
    const getLabel = isError
        ? isBuy
            ? "Did not buy"
            : "Did not sell"
        : isSuccess
          ? isBuy
              ? "Bought"
              : "Sold"
          : isPendingOrLater
            ? isBuy
                ? "Buying..."
                : "Selling..."
            : "Buy";
    // postSuccess switches You Get back to display mode after the receipt
    // collapses.
    const getMode: "compact" | "display" = isPostSuccess
        ? "display"
        : isPendingOrLater
          ? "compact"
          : "display";
    const sendMode: "compact" | "display" | "input" = isReceiptOrLater
        ? "compact"
        : "input";

    // Receipt rows — sourced from the snapshot so values stay frozen across
    // pending → processing → success transitions. Mirrors the BuyWidget
    // pattern (Price / Price Impact / Fee / Network Cost).
    //
    const receiptRows: ReadonlyArray<{ label: string; value: string }> =
        snapshot
            ? [
                  {
                      label: "Price",
                      value: `${snapshot.receiveAmount} ${snapshot.receiveSymbol}`
                  },
                  {
                      label: "Price Impact",
                      value: snapshot.priceImpactDisplay
                  },
                  { label: "Routing Fee", value: snapshot.routingFeeDisplay },
                  {
                      label: "Network Cost",
                      value: snapshot.networkFeeDisplay
                  }
              ]
            : [];

    const receiptSummaryLabel = isError
        ? isBuy
            ? "Did not buy"
            : "Did not sell"
        : isSuccess
          ? isBuy
              ? "Bought"
              : "Sold"
          : isBuy
            ? "Buying..."
            : "Selling...";
    const receiptSummaryValue = snapshot
        ? `${snapshot.receiveAmount} ${snapshot.receiveSymbol}`
        : "";

    const buySuccessLabel = getSwapSuccessLabel({ mode: "buy", snapshot });
    const sellSuccessLabel = getSwapSuccessLabel({ mode: "sell", snapshot });

    // Phase-driven button label / status. Connect / not-whitelisted gating
    // continues to flow through TransactionCTA; otherwise we render a
    // phase-aware Button (or TransitionButton on success). Error wording
    // mirrors the showcase BuyFlow ("Buy Failed" / "Sell Failed").
    //
    // Pre-submit + during-flight labels include the USDv amount so the
    // button reads as a progressive verb form: Buy [x] → Buying [x] →
    // Bought [x] (Sell mirrors the same shape). The USDv is on the
    // receive leg for Buy and the send leg for Sell. Snapshot wins
    // during/after submit so the value stays stable; pre-submit falls
    // back to the live displays.
    const usdvLabelAmount = getSwapUsdVLabelAmount({
        mode: swap.mode,
        snapshot,
        sendAmount: swap.sendAmount,
        receiveAmountDisplay: swap.receiveAmountDisplay
    });
    const buttonLabel = getSwapCtaLabel({
        phase,
        mode: swap.mode,
        usdvAmount: usdvLabelAmount,
        hasInput: sendParsed > 0,
        ctaFeedback: swap.ctaFeedback?.message ?? null
    });

    const buttonStatus: "error" | "pending" | undefined = isError
        ? "error"
        : isPending || isProcessing
          ? "pending"
          : undefined;

    const txError = isError ? swap.executeState.errorMessage : null;

    // Submit button — Buy chains a Stake CTA post-success via the design
    // system's own `labels`/`labelKey` swap; Sell and the error path close
    // back to the form and still route through `TransitionButton`, because
    // the DS swap is opacity-only and cannot express their scale + blur
    // crossfade. Regular Button otherwise.
    const renderSubmitButton = (): React.ReactElement => {
        if (!swap.connected) {
            return (
                <TransactionCTA
                    connected={swap.connected}
                    isChecked={isChecked}
                    isAllowed={isAllowed}
                    onConnect={(): void => {
                        openModal("swap_widget");
                    }}
                    onClick={() => void onSubmit()}
                    disabled={submitDisabled}
                >
                    {swap.ctaFeedback?.message ??
                        (isBuy ? "Buy USDv" : "Sell USDv")}
                </TransactionCTA>
            );
        }
        if (isChecked && !isAllowed) {
            return (
                <TransactionCTA
                    connected={swap.connected}
                    isChecked={isChecked}
                    isAllowed={isAllowed}
                    onConnect={(): void => {
                        openModal("swap_widget");
                    }}
                    onClick={() => void onSubmit()}
                    disabled={submitDisabled}
                >
                    {swap.ctaFeedback?.message ??
                        (isBuy ? "Buy USDv" : "Sell USDv")}
                </TransactionCTA>
            );
        }
        if (isError) {
            // 2s after entering error, "Buy/Sell Failed" → "Return to
            // Buy/Sell" via fade-shrink-blur. Click runs
            // `swap.handlePendingCancel()` which already preserves
            // sendAmount (only resets executeState + snapshot + view).
            return (
                <TransitionButton
                    showCta={errorReturnReady}
                    successLabel={isBuy ? "Buy Failed" : "Sell Failed"}
                    ctaLabel={isBuy ? "Return to Buy" : "Return to Sell"}
                    initialStatus="error"
                    transition="fade-shrink-blur"
                    onClick={() => swap.handlePendingCancel()}
                />
            );
        }
        if (isSuccess) {
            // Success → 2s after success, "Bought/Sold X USDv" → a
            // return action. Click runs
            // `swap.handleSuccessClose()` which clears the input and
            // returns to the form view.
            return (
                <TransitionButton
                    showCta={successReturnReady}
                    successLabel={isBuy ? buySuccessLabel : sellSuccessLabel}
                    ctaLabel={isBuy ? "Return to Buy" : "Return to Sell"}
                    initialStatus="success"
                    transition="fade-shrink-blur"
                    onClick={swap.handleSuccessClose}
                />
            );
        }
        return (
            <Button
                variant="raised"
                size="lg"
                interaction="light-bar"
                fullWidth
                status={buttonStatus}
                disabled={submitDisabled || isPendingOrLater}
                {...simForce(swap.demoSubmitState)}
                onClick={() => void onSubmit()}
            >
                {buttonLabel}
            </Button>
        );
    };

    return (
        <div
            // Sim mode only: make the root the cursor's positioned container.
            // In production `sim.enabled` is false → no ref, no `relative`,
            // no overlay — byte-identical render to before.
            ref={sim.enabled ? setContainerRef : undefined}
            className={`flex flex-col gap-3 md:gap-6 flex-1${
                sim.enabled ? " relative" : ""
            }`}
        >
            <style
                dangerouslySetInnerHTML={{ __html: RECEIPT_KEYFRAMES_CSS }}
            />
            <TabsMenu value={activeSubTab} onValueChange={handleSubTabChange}>
                <TabTrigger
                    value="buy"
                    size="md"
                    className="md:px-6 md:py-3 md:underline-offset-[6px]"
                >
                    Buy
                </TabTrigger>
                <TabTrigger
                    value="sell"
                    size="md"
                    className="md:px-6 md:py-3 md:underline-offset-[6px]"
                >
                    Sell
                </TabTrigger>
            </TabsMenu>

            {/* Sunken swap form. */}
            <div className="flex flex-col flex-1">
                <div className="border border-line-base bg-surface-ground p-3 md:p-6 flex flex-col flex-1">
                    {/* You Send — collapses in pending+ */}
                    <div
                        data-sim-cursor-key="amount"
                        className="mb-4 md:mb-5"
                        style={collapseStyle(isPendingOrLater, {
                            maxHeight: 200,
                            marginBottomFromClass: true,
                            duration: 600,
                            opacityDuration: 400
                        })}
                    >
                        <AmountInput
                            label={sendLabel}
                            value={displaySendAmount}
                            onChange={
                                isPendingOrLater
                                    ? undefined
                                    : (v: string): void =>
                                          swap.handleSendAmountChange(v)
                            }
                            onBlur={() => swap.handleBlur()}
                            placeholder="0"
                            tokenChip={
                                <div
                                    className={
                                        sendHasChev && !isPendingOrLater
                                            ? "cursor-pointer"
                                            : ""
                                    }
                                    onClick={
                                        sendHasChev && !isPendingOrLater
                                            ? (): void => {
                                                  capture(
                                                      "swap_token_picker_opened",
                                                      {
                                                          mode: swap.mode,
                                                          field: "send",
                                                          current_token_symbol:
                                                              displaySendSymbol
                                                      }
                                                  );
                                                  setSendTokenSelectorOpen(
                                                      true
                                                  );
                                              }
                                            : undefined
                                    }
                                >
                                    <TokenChip
                                        icon={displaySendIcon}
                                        symbol={displaySendSymbol}
                                        rounded={sendRounded}
                                        selectable={
                                            sendHasChev && !isPendingOrLater
                                        }
                                    />
                                </div>
                            }
                            seekable
                            {...sendSeek}
                            maxDecimals={swap.sendTokenDecimals}
                            rejectionTrigger={swap.rejectionCount}
                            secondary={`$${swap.usdValue}`}
                            balance={swap.sendDisplayBalance}
                            onBalanceClick={
                                isPendingOrLater
                                    ? undefined
                                    : swap.handleMaxClick
                            }
                            mode={sendMode}
                        />
                    </div>

                    {/* Rate row — collapses in pending+ */}
                    <div
                        className="max-h-10 md:max-h-13 mb-4 md:mb-5"
                        style={collapseStyle(isPendingOrLater, {
                            maxHeightFromClass: true,
                            marginBottomFromClass: true,
                            duration: 400,
                            opacityDuration: 200
                        })}
                    >
                        <RateRow
                            rate={rateLabel}
                            onToggle={swap.handleSwapClick}
                        />
                    </div>

                    {/* You Get + Receipt + Inline error — flex-col flex-1 so this
                        section absorbs the leftover space inside the sunken when
                        You Send + Rate collapse. Mirrors the showcase BuyFlow's
                        three-layer flex-1 chain (children wrapper → sunken →
                        get-section). */}
                    <div className="flex flex-col flex-1">
                        <AmountInput
                            label={getLabel}
                            value={displayReceiveAmount}
                            placeholder="0"
                            secondary={`$${swap.receiveUsdValue}`}
                            tokenChip={
                                <div
                                    className={
                                        receiveHasChev && !isPendingOrLater
                                            ? "cursor-pointer"
                                            : ""
                                    }
                                    onClick={
                                        receiveHasChev && !isPendingOrLater
                                            ? (): void => {
                                                  capture(
                                                      "swap_token_picker_opened",
                                                      {
                                                          mode: swap.mode,
                                                          field: "receive",
                                                          current_token_symbol:
                                                              displayReceiveSymbol
                                                      }
                                                  );
                                                  setReceiveTokenSelectorOpen(
                                                      true
                                                  );
                                              }
                                            : undefined
                                    }
                                >
                                    <TokenChip
                                        icon={displayReceiveIcon}
                                        symbol={displayReceiveSymbol}
                                        rounded={receiveRounded}
                                        selectable={
                                            receiveHasChev && !isPendingOrLater
                                        }
                                    />
                                </div>
                            }
                            mode={getMode}
                            loading={
                                swap.isQuoteFetching &&
                                sendParsed > 0 &&
                                !hasQuoteRouteError &&
                                !isPendingOrLater
                            }
                            balance={swap.receiveDisplayBalance}
                            errorMessage={
                                hasQuoteRouteError && !isPendingOrLater
                                    ? "Quote unavailable"
                                    : undefined
                            }
                        />

                        {/* Receipt mounts in receipt phases and animates from
                            a collapsed state to prevent a layout jump. */}
                        {isReceiptOrLater && snapshot && (
                            <div
                                style={collapseStyle(
                                    !receiptExpanded || isPostSuccess,
                                    {
                                        maxHeight: 300,
                                        marginTop: 22.5,
                                        duration: 500,
                                        opacityDuration: 400
                                    }
                                )}
                            >
                                <ReceiptBlock
                                    rows={receiptRows}
                                    summaryLabel={receiptSummaryLabel}
                                    summaryValue={receiptSummaryValue}
                                    staggerDelay={isPending ? 80 : undefined}
                                />
                            </div>
                        )}

                        {/* Inline error — always rendered with a non-breaking
                            space placeholder so it can smoothly slide in/out via
                            collapseStyle. Mirrors the showcase BuyFlow error
                            pattern (TradeWidgetShowcase.tsx:425–432). */}
                        <Text
                            variant="small"
                            render={<p />}
                            className="text-error-base"
                            style={collapseStyle(!isError, {
                                marginTop: 16,
                                duration: 300
                            })}
                        >
                            {isError && txError ? txError : "\u00A0"}
                        </Text>
                    </div>
                </div>
            </div>

            <div data-sim-cursor-key="submit">{renderSubmitButton()}</div>

            {TokenSelectorComponent && (
                <>
                    <TokenSelectorComponent
                        options={swap.sendAssetOptions}
                        excludedMints={LEGACY_USDV_MINTS}
                        value={swap.sendCurrency}
                        onChange={(value) => {
                            if (value === USDV_MINT && swap.mode === "buy") {
                                swap.handleSellTab();
                                return;
                            }
                            swap.handleCurrencyChange("send", value);
                        }}
                        isOpen={sendTokenSelectorOpen}
                        onToggle={() => (
                            capture("swap_token_picker_closed", {
                                field: "send"
                            }),
                            setSendTokenSelectorOpen(!sendTokenSelectorOpen)
                        )}
                        onSelectOption={({
                            option
                        }: {
                            option: SelectOption;
                        }) => {
                            if (
                                option.value === USDV_MINT &&
                                swap.mode === "buy"
                            ) {
                                return;
                            }
                            swap.setSendSelectedOption(option);
                        }}
                    />
                    <TokenSelectorComponent
                        options={swap.receiveAssetOptions}
                        excludedMints={LEGACY_USDV_MINTS}
                        value={swap.receiveCurrency}
                        onChange={(value) => {
                            if (value === USDV_MINT && swap.mode === "sell") {
                                swap.handleBuyTab();
                                return;
                            }
                            swap.handleCurrencyChange("receive", value);
                        }}
                        isOpen={receiveTokenSelectorOpen}
                        onToggle={() =>
                            setReceiveTokenSelectorOpen(
                                !receiveTokenSelectorOpen
                            )
                        }
                        onSelectOption={({
                            option
                        }: {
                            option: SelectOption;
                        }) => {
                            if (
                                option.value === USDV_MINT &&
                                swap.mode === "sell"
                            ) {
                                return;
                            }
                            swap.setReceiveSelectedOption(option);
                        }}
                    />
                </>
            )}

            {/* Simulated cursor overlay — sim mode only (no pointer events,
                high z). Absent entirely in production. */}
            {sim.enabled && cursor.layer}
        </div>
    );
}

export const SwapContent = React.memo(SwapContentImpl);
